import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { EmployeeService } from '../../services/employee.service';
import { ShiftSchedule, ShiftScheduleFilters } from '../../models/employee.models';
import { SearchbarComponent } from '../../../shared/components/searchbar/searchbar.component';
import { AddButtonComponent } from '../../../shared/components/add-button/add-button.component';
import { EmptyResultComponent } from '../../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../shared/components/pagination-bar/pagination-bar.component';
import {
  ScheduleShiftModalComponent,
  ScheduleShiftData,
} from '../../../shared/components/schedule-shift-modal/schedule-shift-modal.component';
import { EditScheduleModalComponent } from '../../../shared/components/edit-schedule-modal/edit-schedule-modal.component';
import { BreakSettingsModalComponent } from '../../../components/break-settings-modal/break-settings-modal.component';
import {
  PeriodDropdownComponent,
  PeriodOption,
  CustomWeekData,
} from '../../../shared/components/period-dropdown/period-dropdown.component';
import { TableUtils, DragScrollConfig } from '../../utils/table.utils';
import { formatDateToISO } from '../../../shared/utils/date.util';

/**
 * Displays and manages employee shift schedules for the currently selected period, including searching, pagination, schedule creation/editing, and break settings.
 * @component
 * @selector app-shift-schedule-tab
 * @example
 *   <app-shift-schedule-tab (scheduleChanged)="handler()"></app-shift-schedule-tab>
 * @output {void} scheduleChanged - Fired after a schedule is created or edited so the parent can refresh employee stats.
 * @lifecycle ngOnInit - Initializes period filter options and sets the default period.
 * @lifecycle ngAfterViewInit - Reinitializes drag scrolling after the view is rendered.
 * @lifecycle ngOnDestroy - Cleans up subscriptions, timers, and table utilities.
 * @method load - Called by the parent to load shift schedules when the tab becomes active.
 * @method reloadAfterShiftChange - Reloads schedules and refreshes the shift modal after changes from the shifts tab.
 * @method loadShiftSchedules - Fetches shift schedules using the current search term, period filter, and pagination settings.
 * @method onShiftScheduleSearchChange - Debounces search input changes before reloading schedules.
 * @method onPageChange - Updates the current page and reloads schedules.
 * @method onItemsPerPageChange - Updates the page size, resets to page 1, and reloads schedules.
 * @method onTogglePeriodDropdown - Toggles the period filter dropdown.
 * @method onApplyPeriod - Applies the selected period filter and reloads schedules.
 * @method onClearPeriod - Resets the period filter to the default current week and reloads schedules.
 * @method onCustomWeekChange - Applies a custom date range and reloads schedules.
 * @method onCreateSchedule - Opens the create schedule modal.
 * @method onCloseScheduleShiftModal - Closes the create schedule modal.
 * @method onSaveScheduleShift - Handles successful schedule creation, shows a success message, reloads schedules, and emits scheduleChanged.
 * @method onEditShiftSchedule - Prepares the selected schedule and opens the edit schedule modal.
 * @method onCloseEditScheduleModal - Closes the edit schedule modal and clears the selected schedule.
 * @method onEditScheduleSaveSuccess - Handles successful schedule edits, reloads schedules, and emits scheduleChanged.
 * @method onDeleteShiftSchedule - Shows a placeholder message for delete functionality not yet implemented.
 * @method onBreakSettings - Opens the break settings modal.
 * @method onCloseBreakSettingsModal - Closes the break settings modal.
 * @method onSaveBreakSettings - Handles successful break settings updates and shows a success message.
 * @method toggleShiftScheduleActionsDropdown - Toggles the row actions dropdown and positions it within the viewport.
 * @method getCreateScheduleAction - Returns a callback used to trigger schedule creation.
 * @method getEmployeeInitialsFromName - Generates employee initials from a full name.
 * @method formatWorkingDaysTimings - Formats working-day and timing strings for display.
 * @method formatTime - Converts 24-hour time strings into 12-hour AM/PM format.
 * @method trackByScheduleId - Tracks schedule rows by schedule ID for efficient rendering.
 */
@Component({
  selector: 'app-shift-schedule-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SearchbarComponent,
    AddButtonComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    PaginationBarComponent,
    ScheduleShiftModalComponent,
    EditScheduleModalComponent,
    BreakSettingsModalComponent,
    PeriodDropdownComponent,
  ],
  providers: [NotificationService],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './shift-schedule-tab.component.html',
})
export class ShiftScheduleTabComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('shiftScheduleTableScrollWrapper') shiftScheduleTableScrollWrapper!: ElementRef;
  @ViewChild(ScheduleShiftModalComponent) scheduleShiftModalComponent!: ScheduleShiftModalComponent;
  @ViewChild(BreakSettingsModalComponent) breakSettingsModal!: BreakSettingsModalComponent;

  /** Fired after a schedule is saved/edited so the parent can refresh employee stats. */
  @Output() scheduleChanged = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private shiftScheduleDragScrollConfig!: DragScrollConfig;
  private shiftScheduleSearchTimeout: ReturnType<typeof setTimeout> | null = null;

  shiftSchedules: ShiftSchedule[] = [];
  filteredShiftSchedules: ShiftSchedule[] = [];
  shiftScheduleSearchTerm = '';
  loadingShiftSchedules = false;

  activeShiftScheduleActionsDropdown: string | null = null;
  selectedShiftScheduleForDropdown: ShiftSchedule | null = null;

  showPeriodDropdown = false;
  periodOptions: PeriodOption[] = [];
  selectedPeriod = '';
  tempSelectedPeriod = '';
  customWeekData: CustomWeekData | null = null;

  showScheduleShiftModal = false;
  showEditScheduleModal = false;
  selectedScheduleForEdit: any = null;

  showBreakSettingsModal = false;

  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  totalPages = 1;

  shimmerColumns: ShimmerColumn[] = [
    { label: 'Employee Name', width: 'md' },
    { label: 'Shifts', width: 'sm' },
    { label: 'Working Days / Timings', width: 'lg' },
    { label: 'Actions', width: 'xs' },
  ];

  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalItems: this.totalItems,
      itemsPerPage: this.itemsPerPage,
    };
  }

  constructor(
    private employeeService: EmployeeService,
    private messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.initPeriodOptions();
    this.setDefaultPeriod();
  }

  ngAfterViewInit(): void {
    this.reinitDragScroll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.shiftScheduleSearchTimeout) clearTimeout(this.shiftScheduleSearchTimeout);
    TableUtils.cleanup();
  }

  /** Called by parent when this tab becomes active or when shifts change. */
  load(): void {
    this.loadingShiftSchedules = true;
    this.loadShiftSchedules();
  }

  /** Called by parent after shift changes from the shifts tab. */
  reloadAfterShiftChange(): void {
    this.loadShiftSchedules();
    this.scheduleShiftModalComponent?.reloadShifts();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.period-dropdown')) this.showPeriodDropdown = false;
    if (!target.closest('.actions-cell')) this.activeShiftScheduleActionsDropdown = null;
  }

  /**
   * Loads shift schedules using the current search term, date range, and pagination settings.
   * @example
   * loadShiftSchedules()
   * void
   * @returns {void} No return value.
   **/
  loadShiftSchedules(): void {
    const filters: ShiftScheduleFilters = {};
    if (this.shiftScheduleSearchTerm.trim()) filters.search = this.shiftScheduleSearchTerm.trim();
    const dateRange = this.getDateRangeForPeriod();
    if (dateRange) {
      filters.week_start = dateRange.startDate;
      filters.week_end = dateRange.endDate;
    }
    filters.page = this.currentPage;
    filters.limit = this.itemsPerPage;

    this.employeeService
      .loadShiftSchedules(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.shiftSchedules = response.data || [];
          this.filteredShiftSchedules = [...this.shiftSchedules];
          this.loadingShiftSchedules = false;
          if (response.pagination) {
            this.currentPage = response.pagination.page || response.pagination.current_page || 1;
            this.totalPages =
              response.pagination.totalPages || response.pagination.total_pages || 1;
            this.totalItems = response.pagination.total || response.pagination.total_items || 0;
            this.itemsPerPage =
              response.pagination.limit || response.pagination.items_per_page || this.itemsPerPage;
          } else {
            this.totalItems = this.shiftSchedules.length;
            this.totalPages = 1;
          }
          this.reinitDragScroll();
        },
        error: () => {
          this.shiftSchedules = [];
          this.filteredShiftSchedules = [];
          this.loadingShiftSchedules = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load shift schedules. Please try again.',
          });
        },
      });
  }

  onShiftScheduleSearchChange(): void {
    if (this.shiftScheduleSearchTimeout) clearTimeout(this.shiftScheduleSearchTimeout);
    this.shiftScheduleSearchTimeout = setTimeout(() => this.loadShiftSchedules(), 300);
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadShiftSchedules();
    }
  }

  onItemsPerPageChange(n: number): void {
    this.itemsPerPage = n;
    this.currentPage = 1;
    this.loadShiftSchedules();
  }

  // Period Dropdown
  onTogglePeriodDropdown(): void {
    this.initPeriodOptions();
    this.showPeriodDropdown = !this.showPeriodDropdown;
  }

  onSelectPeriod(_period: string): void {}

  onApplyPeriod(period: string): void {
    const opt = this.periodOptions.find(o => o.id === period);
    this.selectedPeriod = opt ? opt.label : period;
    this.showPeriodDropdown = false;
    this.loadShiftSchedules();
  }

  onClearPeriod(): void {
    this.setDefaultPeriod();
    this.showPeriodDropdown = false;
    this.loadShiftSchedules();
  }

  onClosePeriodDropdown(): void {
    this.showPeriodDropdown = false;
  }

  onCustomWeekChange(data: CustomWeekData): void {
    this.customWeekData = data;
    this.selectedPeriod = data.weekRange;
    this.loadShiftSchedules();
  }

  isApplyButtonEnabled(): boolean {
    return this.tempSelectedPeriod !== this.selectedPeriod;
  }

  // Create / Edit Schedule
  onCreateSchedule(): void {
    this.showScheduleShiftModal = true;
  }

  onCloseScheduleShiftModal(): void {
    this.showScheduleShiftModal = false;
  }

  onSaveScheduleShift(_data: ScheduleShiftData): void {
    this.showScheduleShiftModal = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Shift schedule created successfully!',
    });
    this.loadShiftSchedules();
    this.scheduleChanged.emit();
  }

  /**
   * Opens the edit schedule modal and populates the selected schedule details for editing.
   * @example
   * nEditShiftSchedule(schedule)
   * void
   * @param {ShiftSchedule} schedule - The shift schedule object to edit.
   * @returns {void} Does not return a value.
   **/
  onEditShiftSchedule(schedule: ShiftSchedule): void {
    this.activeShiftScheduleActionsDropdown = null;
    this.selectedShiftScheduleForDropdown = null;
    const dateRange = this.getDateRangeForPeriod();
    this.selectedScheduleForEdit = {
      schedule_id: schedule.schedule_id,
      employee_id: schedule.employee_id,
      employee_name: schedule.employee_name,
      emp_id: schedule.emp_id,
      designation: schedule.designation || 'Sales Associate',
      picture_url: schedule.picture_url,
      week_range: schedule.week_range,
      week_start: dateRange?.startDate || this.getCurrentWeekRange(new Date()).startDate,
      week_end: dateRange?.endDate || this.getCurrentWeekRange(new Date()).endDate,
      shift_id: schedule.shift_id,
      shift_name: schedule.shift_name,
      working_days: schedule.working_days,
    };
    this.showEditScheduleModal = true;
  }

  onCloseEditScheduleModal(): void {
    this.showEditScheduleModal = false;
    this.selectedScheduleForEdit = null;
  }

  onEditScheduleSaveSuccess(): void {
    this.showEditScheduleModal = false;
    this.selectedScheduleForEdit = null;
    this.loadShiftSchedules();
    this.scheduleChanged.emit();
  }

  onDeleteShiftSchedule(_schedule: ShiftSchedule): void {
    this.activeShiftScheduleActionsDropdown = null;
    this.selectedShiftScheduleForDropdown = null;
    this.messageService.add({
      severity: 'info',
      summary: 'Coming Soon',
      detail: 'Delete shift schedule functionality will be implemented soon.',
    });
  }

  // Break Settings
  onBreakSettings(): void {
    this.showBreakSettingsModal = true;
  }

  onCloseBreakSettingsModal(): void {
    this.showBreakSettingsModal = false;
  }

  onSaveBreakSettings(_settings: any): void {
    this.showBreakSettingsModal = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Break settings saved successfully!',
    });
  }

  toggleShiftScheduleActionsDropdown(event: Event, scheduleId: string): void {
    if (this.activeShiftScheduleActionsDropdown === scheduleId) {
      this.activeShiftScheduleActionsDropdown = null;
      this.selectedShiftScheduleForDropdown = null;
    } else {
      this.activeShiftScheduleActionsDropdown = scheduleId;
      this.selectedShiftScheduleForDropdown =
        this.shiftSchedules.find(s => s.schedule_id === scheduleId) || null;
      this.positionDropdown(event, '.actions-dropdown-menu[id*="shift-schedule-dropdown"]');
    }
  }

  getCreateScheduleAction(): () => void {
    return () => this.onCreateSchedule();
  }

  getEmployeeInitialsFromName(fullName: string): string {
    const names = fullName.split(' ');
    if (names.length >= 2) return (names[0].charAt(0) + names[1].charAt(0)).toUpperCase();
    return fullName.charAt(0).toUpperCase();
  }

  formatWorkingDaysTimings(workingDays: string): string {
    if (!workingDays) return '';
    const parts = workingDays.split(' ');
    if (parts.length >= 6)
      return `${parts[0]} - ${parts[2]} ${this.formatTime(parts[3])} - ${this.formatTime(parts[5])}`;
    if (parts.length >= 4)
      return `${parts[0]} - ${parts[2]} ${this.formatTime(parts[3])} - ${this.formatTime(parts[5] || parts[4])}`;
    return workingDays;
  }

  /**
   * Formats a time string from 24-hour HH:MM format into 12-hour time with AM/PM.
   * @example
   * formatTime('13:30')
   * '1:30 PM'
   * @param {string} t - Time string to format.
   * @returns {string} Formatted time string, or an empty string if no input is provided.
   */
  formatTime(t: string): string {
    if (!t) return '';
    const parts = t.split(':');
    if (parts.length >= 2) {
      let h = parseInt(parts[0]);
      const m = parts[1];
      const ampm = h >= 12 ? 'PM' : 'AM';
      if (h === 0) h = 12;
      else if (h > 12) h -= 12;
      return `${h}:${m} ${ampm}`;
    }
    return t;
  }

  trackByScheduleId(_: number, schedule: ShiftSchedule): string {
    return schedule.schedule_id;
  }

  /**
   * Initializes the available period options for the shift schedule tab based on the current, next, and previous week.
   * @example
   * initPeriodOptions()
   * void
   * @returns {void} Does not return a value.
   **/
  private initPeriodOptions(): void {
    const today = new Date();
    const curr = this.getWeekDates(today);
    const next = this.getWeekDates(new Date(today.getTime() + 7 * 86400000));
    const prev = this.getWeekDates(new Date(today.getTime() - 7 * 86400000));
    this.periodOptions = [
      { id: 'current', label: `Current Week, ${curr.start} - ${curr.end}`, value: 'current' },
      { id: 'next', label: `Next Week, ${next.start} - ${next.end}`, value: 'next' },
      { id: 'previous', label: `Previous Week, ${prev.start} - ${prev.end}`, value: 'previous' },
      { id: 'custom', label: 'Custom week', value: 'custom' },
    ];
  }

  private setDefaultPeriod(): void {
    this.initPeriodOptions();
    const curr = this.periodOptions.find(o => o.value === 'current');
    if (curr) {
      this.selectedPeriod = curr.label;
      this.tempSelectedPeriod = curr.label;
    }
  }

  /**
   * Gets the date range for the currently selected period or custom week data.
   * @example
   * getDateRangeForPeriod()
   * { startDate: '2026-04-05', endDate: '2026-04-11' }
   * @returns {{ startDate: string; endDate: string } | null} The computed date range for the selected period, or null if unavailable.
   **/
  private getDateRangeForPeriod(): { startDate: string; endDate: string } | null {
    const today = new Date();
    if (this.customWeekData)
      return { startDate: this.customWeekData.startDate, endDate: this.customWeekData.endDate };
    const opt = this.periodOptions.find(o => o.label === this.selectedPeriod);
    const id = opt?.id || 'current';
    switch (id) {
      case 'current':
        return this.getCurrentWeekRange(today);
      case 'next':
        return this.getCurrentWeekRange(new Date(today.getTime() + 7 * 86400000));
      case 'previous':
        return this.getCurrentWeekRange(new Date(today.getTime() - 7 * 86400000));
      default:
        return this.getCurrentWeekRange(today);
    }
  }

  private getCurrentWeekRange(date: Date): { startDate: string; endDate: string } {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { startDate: formatDateToISO(start), endDate: formatDateToISO(end) };
  }

  private getWeekDates(date: Date): { start: string; end: string } {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return {
      start: `${months[start.getMonth()]} ${start.getDate()}`,
      end: `${months[end.getMonth()]} ${end.getDate()}`,
    };
  }

  /**
   * Positions a dropdown menu relative to an action button and keeps it within the viewport.
   * @example
   * positionDropdown(event, '.dropdown-menu')
   * undefined
   * @param {Event} event - The triggering DOM event used to locate the related action button.
   * @param {string} selector - CSS selector used to find the dropdown element to position.
   * @returns {void} This function does not return a value.
   **/
  private positionDropdown(event: Event, selector: string): void {
    requestAnimationFrame(() => {
      const button = (event.target as HTMLElement).closest('.actions-button') as HTMLElement;
      const dropdown = document.querySelector(selector) as HTMLElement;
      if (button && dropdown) {
        const rect = button.getBoundingClientRect();
        const dropdownHeight = dropdown.offsetHeight || 88;
        const viewportHeight = window.innerHeight;
        dropdown.style.position = 'fixed';
        dropdown.style.zIndex = '9999';
        if (viewportHeight - rect.bottom >= dropdownHeight + 10) {
          dropdown.style.top = `${rect.bottom + 5}px`;
          dropdown.style.bottom = 'auto';
        } else {
          dropdown.style.top = 'auto';
          dropdown.style.bottom = `${viewportHeight - rect.top + 5}px`;
        }
        dropdown.style.left = `${rect.right - dropdown.offsetWidth}px`;
      }
    });
  }

  private reinitDragScroll(): void {
    setTimeout(() => {
      if (this.shiftScheduleTableScrollWrapper) {
        this.shiftScheduleDragScrollConfig = TableUtils.createDragScrollConfig(
          this.shiftScheduleTableScrollWrapper
        );
        TableUtils.initializeDragScrolling(this.shiftScheduleDragScrollConfig);
      }
    }, 100);
  }
}
